import { createContext, useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { FCWithChildren, useEffect, useMemo, useState } from 'react';
import { getMySizesPayload } from '@hooks/catalog-context/use-fetch-products';
import { useQuery } from '@tanstack/react-query';
import { ProductCatalogQueryKeys } from '@enums/react-query-keys';
import logger from '@helpers/utils/logger/client';
import SearchService, { ProductSearchHeaders } from '@services/search-service';
import { mapCountryToTheSizeType } from '@helpers/utils/catalog/catalog-url-helpers';
import { usePreferences } from '@context/preferences.context';
import { SearchInnerFeedResponse } from '@interfaces/api/responses/search-inner-feed-response';
import { InnerFeedContext } from '@interfaces/models/elasticSearch';
import mapCatalogHitToProduct from '@helpers/utils/catalog/map-catalog-hit-to-product';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { useBucket } from '@context/bucket.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { readLocalStorageJson } from '@hooks/use-local-storage';
import { Product } from '@interfaces/models/product';
import { getUserToken } from '@helpers/utils/catalog/catalog-tracking-utils';
import useUser from '@hooks/user/use-user';
import useInnerFeedContextParam from '@hooks/catalog-context/use-inner-feed-context-param';

export interface PLPInnerFeedContextProps {
  showPlpInnerFeed: boolean;
  displayAfterPosition: number | null;
  plpInnerFeedItems: CatalogProduct[];
  plpInnerFeedType: string;
  innerFeedTranslations?: { title: string; subTitle: string };
}

export const PLPInnerFeedContext = createContext<PLPInnerFeedContextProps>({} as never);

export const getRecentlyViewedProductIDs = () => {
  const recentlyViewedProductIDs = readLocalStorageJson<Product[]>('localsave/recentlySeenProducts', []).map(
    (x) => x?.id,
  );
  // we only use the last 10 recently viewed products, i.e. the first 10 in the array
  return recentlyViewedProductIDs.slice(0, 10);
};

export const getValidDisplayAfterPosition = (displayAfterPosition: number | null, rowCount: number) => {
  // since displayAfterPosition is normally based on mobile grid layout, we need to find the corresponding position in desktop grid layout
  // find the nearest number to displayAfterPosition that is divisible by rowCount
  if (displayAfterPosition === null) {
    return null;
  }
  const remainder = displayAfterPosition % rowCount;
  if (remainder === 0) {
    return displayAfterPosition;
  }
  return displayAfterPosition + (rowCount - remainder);
};

const usePlpInnerFeedQuery = (innerFeedContext: InnerFeedContext) => {
  const searchQuery = useContextSelector(CatalogContext, (v) => v.searchQuery);
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const sortBy = useContextSelector(CatalogContext, (v) => v.sortBy);
  const mySizesState = useContextSelector(CatalogContext, (v) => v.mySizesState);
  const { user } = useUser();

  const { country, currency, language, regionName } = usePreferences();

  const requestPayload = useMemo(
    () => ({
      q: searchQuery,
      context: innerFeedContext,
      sortBy,
      filters,
      locale: {
        country,
        currency,
        language,
        sizeType: mapCountryToTheSizeType(regionName),
      },
      mySizes: getMySizesPayload(mySizesState),
    }),
    [searchQuery, sortBy, filters, mySizesState, innerFeedContext],
  );

  const { refetch } = useQuery<SearchInnerFeedResponse>({
    queryKey: [ProductCatalogQueryKeys.GET_PLP_INNER_FEED, requestPayload],
    queryFn: () => {
      return SearchService.plpInnerFeedSearch(requestPayload, getRecentlyViewedProductIDs(), {
        [ProductSearchHeaders.userToken]: getUserToken(user?.id),
      });
    },
    onError: (err) => logger.error(err, 'Error fetching products!'),
    enabled: false, // enabled is set to false here because we will imperatively fetch the feed in the PLPInnerFeedContextProvider
  });

  return { fetchPlpInnerFeed: refetch };
};

export const PLPInnerFeedContextProvider: FCWithChildren = ({ children }) => {
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const [displayAfterPosition, setDisplayAfterPosition] = useState<number | null>(null);
  const [plpInnerFeedItems, setPlpInnerFeedItems] = useState<CatalogProduct[]>([]);
  const [plpInnerFeedType, setPlpInnerFeedType] = useState<string | null>(null);
  const [innerFeedTranslations, setInnerFeedTranslations] = useState<{ title: string; subTitle: string } | null>(null);
  const { country, currency, language } = usePreferences();
  const { sendAnalyticEvent } = useAnalyticEvents('catalog');
  const innerFeedContext = useInnerFeedContextParam();

  const { isFeatureEnabled } = useBucket();
  const isRecentSearchBreakerEnabled =
    innerFeedContext === InnerFeedContext.RecentSearches && isFeatureEnabled((f) => f.enablePlpBreakerWeb, true);
  const isRealTimeRecoBreakerEnabled =
    innerFeedContext === InnerFeedContext.GenericPLP && isFeatureEnabled((f) => f.enableRerecPlpBreaker, true);

  const { fetchPlpInnerFeed } = usePlpInnerFeedQuery(innerFeedContext);

  const resetState = () => {
    setDisplayAfterPosition(null);
    setPlpInnerFeedItems([]);
    setPlpInnerFeedType(null);
  };

  useEffect(() => {
    if (
      !isRecentSearchBreakerEnabled &&
      !isRealTimeRecoBreakerEnabled &&
      innerFeedContext !== InnerFeedContext.DealHunting
    ) {
      return;
    }
    // if the queryResult contains innerFeedConfig, we fetch innerFeed products
    if (queryResult && queryResult.innerFeedConfig) {
      const queryResultDisplayAfterPosition = queryResult.innerFeedConfig.displayAfterPosition;
      if (typeof queryResultDisplayAfterPosition === 'number') {
        setDisplayAfterPosition(queryResultDisplayAfterPosition);
      } else {
        setDisplayAfterPosition(null);
      }
      fetchPlpInnerFeed().then((data) => {
        setPlpInnerFeedItems(
          data?.data?.items
            ? data.data.items.map((product) => mapCatalogHitToProduct(product, country, currency, language))
            : [],
        );
        setPlpInnerFeedType(data?.data?.innerFeed?.type ?? null);
        const title = data?.data?.innerFeed?.title;
        const subTitle = data?.data?.innerFeed?.subtitle;
        setInnerFeedTranslations({ title, subTitle });
      });
      return;
    }
    // if the queryResult does not contain innerFeedConfig, we reset the state
    resetState();
  }, [queryResult]);

  const showPlpInnerFeed =
    displayAfterPosition !== null &&
    plpInnerFeedItems.length > 0 &&
    plpInnerFeedType !== null &&
    !!innerFeedTranslations?.title;

  useEffect(() => {
    if (showPlpInnerFeed) {
      sendAnalyticEvent('app_breaker_availability', {
        label: `app_breaker_${plpInnerFeedType}`,
        property: JSON.stringify(getRecentlyViewedProductIDs()),
      });
    }
    // retrigger the event when the plpInnerFeedItems changes
  }, [showPlpInnerFeed, plpInnerFeedItems]);

  return (
    <PLPInnerFeedContext.Provider
      value={{
        showPlpInnerFeed,
        displayAfterPosition,
        plpInnerFeedItems,
        plpInnerFeedType,
        innerFeedTranslations,
      }}
    >
      {children}
    </PLPInnerFeedContext.Provider>
  );
};
